// @ts-ignore
// @ts-nocheck

import React from "react";
import Slugify from "slugify";
import { saveAs } from "file-saver";
import { format } from "@maplibre/maplibre-gl-style-spec";
import type { StyleSpecification } from "maplibre-gl";
import { MdFileDownload } from "react-icons/md";
import { WithTranslation, withTranslation } from "react-i18next";

import FieldString from "./FieldString";
import InputButton from "./InputButton";
import Modal from "./Modal";
import style from "../libs/style";
import fieldSpecAdditional from "../libs/field-spec-additional";

type ModalExportInternalProps = {
  mapStyle: StyleSpecification & { id: string };
  onStyleChanged(...args: unknown[]): unknown;
  isOpen: boolean;
  onOpenToggle(...args: unknown[]): unknown;
} & WithTranslation;
type ModalExportProps = {
  tokenKey: string;
  exportName: string;
};

class ModalExportInternal extends React.Component<
  ModalExportInternalProps,
  ModalExportProps
> {
  constructor(props: ModalExportInternalProps) {
    super(props);
    this.state = {
      tokenKey: "",
      exportName: this.getDefaultExportName(),
      error: null,
    };
  }
  clearError() {
    this.setState({
      error: null,
    });
  }
  deepClone(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }

  validateTokenKey() {
    if (!this.state.tokenKey.trim()) {
      this.setState({
        error: "Token is required. Please enter a valid token.",
      });
      return false;
    }
    return true;
  }

  tokenizedStyle() {
    const updatedStyle = {
      ...this.props.mapStyle,
      metadata: {
        ...(this.props.mapStyle.metadata || {}),
        "atlas-studio:mapmetrics_access_token": this.state.tokenKey,
      },
    };
    return format(
      style.stripAccessTokens(style.replaceAccessTokens(updatedStyle))
    );
  }

  getDefaultExportName() {
    if (this.props.mapStyle.name) {
      return Slugify(this.props.mapStyle.name, {
        replacement: "_",
        remove: /[*\-+~.()'"!:]/g,
        lower: true,
      });
    } else {
      return "Untitled Style";
    }
  }

  exportName() {
    if (this.state.exportName && this.state.exportName.trim()) {
      return Slugify(this.state.exportName, {
        replacement: "_",
        remove: /[*\-+~.()'"!:]/g,
        lower: true,
      });
    } else {
      return this.getDefaultExportName();
    }
  }

  downloadHtml() {
    this.clearError();
    if (!this.validateTokenKey()) return;

    const htmlTitle = this.state.exportName || this.props.mapStyle.name || this.props.t("Map");

    // Get the style and ensure it's valid JSON
    let styleData = this.tokenizedStyle();

    try {
      // If styleData is a JSON string, parse it
      if (typeof styleData === "string") {
        styleData = JSON.parse(styleData);
      }
    } catch (error) {
      this.setState({
        error: error.toString(),
      });
      return;
    }

    if (typeof styleData !== "object") {
      console.error("tokenizedStyle did not return a JSON object:", styleData);
      alert("Invalid style format.");
      return;
    }
    console.log(styleData, "styleData");
    // Replace the token in the 'tiles' property
    if (styleData.sources && styleData.sources.protomaps) {
      const protomaps = styleData.sources.protomaps;

      if (Array.isArray(protomaps.tiles)) {
        protomaps.tiles = protomaps.tiles.map((tile) =>
          tile.replace(/token=[^&]+/, `token=${this.state.tokenKey}`)
        );
      }
    }

    // Update the style name to match the export name
    if (this.state.exportName && this.state.exportName.trim()) {
      styleData.name = this.state.exportName.trim();
    }

    const updatedStyle = JSON.stringify(styleData);

    console.log("Updated style:", updatedStyle);

    const html = `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${htmlTitle}</title>
    <meta name="viewport" content="initial-scale=1,maximum-scale=1,user-scalable=no" />
    <link
      rel="stylesheet"
      href="https://gateway.mapmetrics.org/assets/css/mapmetrics-gl.css"
    />
    <script src="https://gateway.mapmetrics.org/assets/js/mapmetrics-gl.js"></script>
    <style>
      body { margin: 0; padding: 0; }
      #map { position: absolute; top: 0; bottom: 0; width: 100%; }
    </style>
  </head>
  <body>
    <div id="map"></div>
    <script>
        const map = new mapmetrics.Map({
           container: 'map',
           style: ${updatedStyle},
        });
        map.addControl(new mapmetrics.NavigationControl());
    </script>
  </body>
  </html>
  `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const exportName = this.exportName();
    saveAs(blob, `${exportName}.html`);
    console.log("Final styles:", updatedStyle);
  }

  downloadStyle() {
    this.clearError();
    if (!this.validateTokenKey()) return;

    // Replace token in the JSON
    let styleData = this.tokenizedStyle();

    try {
      // If styleData is a JSON string, parse it
      if (typeof styleData === "string") {
        styleData = JSON.parse(styleData);
      }
    } catch (error) {
      this.setState({
        error: error.toString(),
      });
      return;
    }

    if (typeof styleData !== "object") {
      console.error("tokenizedStyle did not return a JSON object:", styleData);
      alert("Invalid style format.");
      return;
    }
    console.log(styleData, "styleData");
    // Replace the token in the 'tiles' property
    if (styleData.sources && styleData.sources.protomaps) {
      const protomaps = styleData.sources.protomaps;

      if (Array.isArray(protomaps.tiles)) {
        protomaps.tiles = protomaps.tiles.map((tile) =>
          tile.replace(/token=[^&]+/, `token=${this.state.tokenKey}`)
        );
      }
    }

    // Update the style name to match the export name
    if (this.state.exportName && this.state.exportName.trim()) {
      styleData.name = this.state.exportName.trim();
    }

    const updatedStyle = JSON.stringify(styleData);

    // Create a Blob with the modified style
    const blob = new Blob([updatedStyle], {
      type: "application/json;charset=utf-8",
    });

    // Generate export name and save the file
    const exportName = this.exportName();
    saveAs(blob, exportName + ".json");
  }

  changeMetadataProperty(property: string, value: string) {
    this.setState({ tokenKey: value }, () => {
      const changedStyle = {
        ...this.props.mapStyle,
        metadata: {
          ...(this.props.mapStyle.metadata || {}),
          [property]: value,
        },
      };
      this.props.onStyleChanged(changedStyle);
    });
  }

  render() {
    const t = this.props.t;
    const fsa = fieldSpecAdditional(t);
    let errorElement;
    if (this.state.error) {
      errorElement = (
        <div className="studio-modal-error">
          {this.state.error}
          <a
            href="#"
            onClick={() => this.clearError()}
            className="studio-modal-error-close"
          >
            ×
          </a>
        </div>
      );
    }
    return (
      <Modal
        data-wd-key="modal:export"
        isOpen={this.props.isOpen}
        onOpenToggle={this.props.onOpenToggle}
        title={t("Export Style")}
        className="studio-export-modal"
      >
        {errorElement}
        <section className="studio-modal-section">
          <h1>{t("Download Style")}</h1>
          <p>{t("Download a JSON style to your computer.")}</p>

          <div>
            <FieldString
              label={t("Export Name")}
              value={this.state.exportName}
              onChange={(value) => {
                this.setState({ exportName: value || "" });
              }}
            />
          </div>

          <div>
            <FieldString
              label={fsa.studio.mapmetrics_access_token.label}
              fieldSpec={fsa.studio.mapmetrics_access_token}
              value={this.state.tokenKey}
              onChange={(value) => {
                this.changeMetadataProperty(
                  "atlas-studio:mapmetrics_access_token",
                  value || ""
                );
              }}
            />
          </div>

          <div className="studio-modal-export-buttons">
            <InputButton
              className="studio-button--primary"
              onClick={this.downloadStyle.bind(this)}
            >
              <MdFileDownload />
              {t("Download Style")}
            </InputButton>

            <InputButton onClick={this.downloadHtml.bind(this)}>
              <MdFileDownload />
              {t("Download HTML")}
            </InputButton>
          </div>
        </section>
      </Modal>
    );
  }
}

const ModalExport = withTranslation()(ModalExportInternal);
export default ModalExport;
